import { NgComponentOutlet } from '@angular/common';
import { ChangeDetectionStrategy, Component, computed, inject, input, Type } from '@angular/core';
import { TranslocoPipe } from '@jsverse/transloco';
import { LucideAngularModule, LUCIDE_ICONS, LucideIconProviderInterface } from 'lucide-angular';
import { EASE_ICON_REGISTRY } from './icon-registry';

export type IconSize = 'xs' | 'sm' | 'md' | 'lg' | 'xl' | '2xl' | '3xl';
export type IconSource = 'lucide' | 'ease';

@Component({
  selector: 'ease-icon',
  standalone: true,
  imports: [NgComponentOutlet, LucideAngularModule, TranslocoPipe],
  changeDetection: ChangeDetectionStrategy.OnPush,
  template: `
    @switch (source()) { @case ('lucide') { @if (lucideIconExists()) {
    <lucide-icon
      [name]="name()"
      [size]="computedSize()"
      [strokeWidth]="strokeWidth()"
      [class]="iconClasses()"
      [style.--icon-fill]="fill() || 'none'"
      class="tw-block tw-leading-none [&_*]:tw-fill-[var(--icon-fill)]"
    />
    } @else {
    <lucide-icon
      name="FileQuestionMark"
      [size]="computedSize()"
      [strokeWidth]="strokeWidth()"
      class="tw-block tw-leading-none tw-text-muted dark:tw-text-muted-inverted"
      [attr.title]="'ui.icon.notFound' | transloco : { name: name() }"
    />
    } } @case ('ease') { @if (easeIconComponent()) {
    <ng-container [ngComponentOutlet]="easeIconComponent()" />
    } @else {
    <lucide-icon
      name="FileQuestionMark"
      [size]="computedSize()"
      [strokeWidth]="strokeWidth()"
      class="tw-block tw-leading-none tw-text-muted dark:tw-text-muted-inverted"
      [attr.title]="'ui.icon.notFound' | transloco : { name: name() }"
    />
    } } }
  `,
  host: {
    class: 'tw-inline-flex tw-items-center tw-justify-center',
    '[class.tw-w-3]': "size() === 'xs'",
    '[class.tw-h-3]': "size() === 'xs'",
    '[class.tw-w-4]': "size() === 'sm'",
    '[class.tw-h-4]': "size() === 'sm'",
    '[class.tw-w-5]': "size() === 'md'",
    '[class.tw-h-5]': "size() === 'md'",
    '[class.tw-w-6]': "size() === 'lg'",
    '[class.tw-h-6]': "size() === 'lg'",
    '[class.tw-w-8]': "size() === 'xl'",
    '[class.tw-h-8]': "size() === 'xl'",
    '[class.tw-w-10]': "size() === '2xl'",
    '[class.tw-h-10]': "size() === '2xl'",
    '[class.tw-w-12]': "size() === '3xl'",
    '[class.tw-h-12]': "size() === '3xl'",
  },
})
export class EaseIcon {
  private readonly lucideIconProviders = inject(LUCIDE_ICONS) as unknown as LucideIconProviderInterface[];

  /** The name of the icon (Lucide icon name or custom icon key) */
  readonly name = input.required<string>();

  /** The size of the icon */
  readonly size = input<IconSize>('md');

  /** The source of the icon: 'lucide' for Lucide icons, 'ease' for custom icons */
  readonly source = input<IconSource>('lucide');

  /** Stroke width for Lucide icons */
  readonly strokeWidth = input(2);

  /** Additional CSS classes for the icon */
  readonly iconClasses = input('');

  /** Fill color for the icon (CSS color value, e.g., 'currentColor', '#fff', 'rgb(...)') */
  readonly fill = input<string | null>(null);

  /** Computed pixel size based on size input */
  readonly computedSize = computed(() => {
    const sizes: Record<IconSize, number> = {
      xs: 12,
      sm: 16,
      md: 20,
      lg: 24,
      xl: 32,
      '2xl': 40,
      '3xl': 48,
    };
    return sizes[this.size()];
  });

  /** Check if the Lucide icon exists in any registered provider */
  readonly lucideIconExists = computed(() => this.lucideIconProviders.some(provider => provider.hasIcon(this.name())));

  /** Get the custom icon component from the registry */
  readonly easeIconComponent = computed((): Type<unknown> | null => {
    const component = EASE_ICON_REGISTRY[this.name()];
    return component ?? null;
  });
}
